import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { Meta } from "admin/types/meta";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { CompanyAdminUser } from "admin/types/companyAdminUser";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "../../constants/constants";
import { PageContext } from "../../types/pageContext";
import { AdminTypes } from "../../constants/adminTypes";
import { CompanyAdminUserRoles } from "../../constants/companyAdminUserRoles";

const key = "companyAdminUser";
const adapter = createEntityAdapter<CompanyAdminUser.CompanyAdminUser>();

const initialStateValues: CompanyAdminUser.CompanyAdminUserState = {
  companyAdminUser: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchCompanyAdminUsers = createAsyncThunk<
  {
    companyAdminUsers: CompanyAdminUser.CompanyAdminUser[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCompanyAdminUsers`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/company_admin_users.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company_admin_users.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      companyAdminUsers: result.data.companyAdminUsers,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postCompanyAdminUser = createAsyncThunk<
  {
    companyAdminUser: CompanyAdminUser.CompanyAdminUser;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postCompanyAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/company_admin_users.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company_admin_users.json`;
    const result = await createWrapper().post(url, {
      company_admin_user: params,
    });
    console.log(result);
    return {
      companyAdminUser: result.data.companyAdminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchCompanyAdminUser = createAsyncThunk<
  {
    companyAdminUser: CompanyAdminUser.CompanyAdminUser;
  },
  {
    context: PageContext.PageContext;
    id: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCompanyAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/company_admin_users/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company_admin_users/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    console.log(result);
    return {
      companyAdminUser: result.data.companyAdminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putCompanyAdminUser = createAsyncThunk<
  {
    companyAdminUser: CompanyAdminUser.CompanyAdminUser;
  },
  { context: PageContext.PageContext; id: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putCompanyAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/company_admin_users/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company_admin_users/${id}.json`;
    const result = await createWrapper().put(url, {
      company_admin_user: params,
    });
    console.log(result);
    return {
      companyAdminUser: result.data.companyAdminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const resendCompanyAdminUserInvitation = createAsyncThunk<
  void,
  { context: PageContext.PageContext; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(
  `${key}/resendCompanyAdminUserInvitation`,
  async (args, { rejectWithValue }) => {
    try {
      const { context, id } = args;
      const url =
        context.adminType === AdminTypes.manager
          ? `${API_BASE_PATH}/manager/company_admin_users/${id}/resend_invitation_mails.json`
          : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company_admin_users/${id}/resend_invitation_mails.json`;
      const result = await createWrapper().post(url);
      console.log(result);
      return;
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
);

/** slice **/
export const companyAdminUserSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setCompanyAdminUser: (state, action) => {
      state.companyAdminUser = action.payload;
    },
    setCompanyAdminUsers: (state, action) => {
      adapter.setAll(state, action.payload.adminUsers);
      state.meta = action.payload.meta;
    },
    clearCompanyAdminUser: (state) => {
      state.companyAdminUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.companyAdminUser };
      })
      .addCase(postCompanyAdminUser.fulfilled, (state, action) => {
        state.companyAdminUser = action.payload.companyAdminUser;
      })
      .addCase(fetchCompanyAdminUsers.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.companyAdminUsers);
        state.meta = action.payload.meta;
      })
      .addCase(fetchCompanyAdminUser.pending, (state, action) => {
        const targetUserId = action.meta.arg.id;
        if (
          state.companyAdminUser &&
          state.companyAdminUser.id !== targetUserId
        ) {
          // 別のユーザーデータを取得するため選択中のユーザーをクリアする
          state.companyAdminUser = null;
        }
      })
      .addCase(fetchCompanyAdminUser.fulfilled, (state, action) => {
        state.companyAdminUser = action.payload.companyAdminUser;
      })
      .addCase(putCompanyAdminUser.fulfilled, (state, action) => {
        state.companyAdminUser = action.payload.companyAdminUser;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: companyAdminUserByIdSelector,
  selectIds: companyAdminUserIdsSelector,
  selectEntities: companyAdminUserEntitiesSelector,
  selectAll: companyAdminUsersSelector,
  selectTotal: totalCompanyAdminUsersSelector,
} = adapter.getSelectors((state: RootState) => state.companyAdminUser);

const stateSelector = (state: RootState) => state[key];

export const companyAdminUserSelector = createSelector(
  stateSelector,
  (state) => state.companyAdminUser,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const companyAdminUsersMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

/** action export **/
export const {
  setCompanyAdminUser,
  setCompanyAdminUsers,
  clearCompanyAdminUser,
} = companyAdminUserSlice.actions;

export const convertCompanyAdminUserFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.role === CompanyAdminUserRoles.admin && params.hotel_codes) {
    delete params.hotel_codes;
  }
  // フロントエンドは現状1つのホテルのみ選択可能
  if (params.hotel_codes) {
    params.hotel_codes = [params.hotel_codes];
  }
  return params;
};

export const convertCompanyAdminUserToFormValues = (data: any) => {
  const value = { ...data };
  if (value.hotel_codes && value.hotel_codes.length > 0) {
    value.hotel_codes = value.hotel_codes[0];
  }
  return value;
};
